import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { CalculationSetupApis } from 'apis/CalculationSetupApis';
import { GroupRoleApis } from 'apis/GroupRoleApis';
import { PlantApi } from 'apis/PlantApis';
import { RewardInfoApis } from 'apis/RewardInfoApis';
import { FormFilter, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import Content from 'layouts/Content';
import { optionStatus } from 'pages/Plant/SaleLine/ModalSaleLinePage';
import { useEffect, useState } from 'react';
import { groupRewardOptions } from 'utils/constants/constant';

interface IProps {
  disabled: boolean;
  isOpen: boolean;
  defaultFilter: any;
  onCancel: () => void;
  onFinish: (values: any) => void;
}

const spanCol = 8;
const FilterRewardInfo = (props: IProps) => {
  const { isOpen, defaultFilter, onCancel, onFinish, disabled } = props;
  const { user } = useUser();
  const [form] = useForm();
  const [optionsPosition, setOptionsPosition] = useState<any>({});
  const [optionsEmployee, setOptionsEmployee] = useState<any>({});
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [rewardData, setRewardData] =
    useState<ILazySelectData>(initLazySelectData);

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantFilterHO(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      const plants = items.map((item: any) => ({
        label: item.id,
        value: item.id,
      }));

      setPlants(plants);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: getComboboxGroupRoleManagement,
    isPending: isPendingGetComboboxGroupRoleManagement,
  } = useMutation({
    mutationFn: CalculationSetupApis.getComboboxGroupRoleManagement,
    onSuccess: (data) => {
      const rewardIds: string[] = [];
      const options = (data?.data?.getGroupRoleManagement?.items || [])
        .filter((item: any) => {
          if (rewardIds.includes(item.rewardId)) return false;
          rewardIds.push(item.rewardId);
          return true;
        })
        .map((item: any) => ({
          ...item,
          id: item.rewardId,
          label: item.rewardId,
          value: item.rewardId,
        }));

      setRewardData({
        options,
        totalItems: data?.data?.getGroupRoleManagement?.pageInfo?.totalItems,
        hasNextPage: data?.data?.getGroupRoleManagement?.pageInfo?.hasNextPage,
      });
    },
  });

  const mutationPosition = useMutation({
    mutationFn: GroupRoleApis.getComboboxJobTitle,
    onSuccess: (res) => {
      const data = res?.data?.getJobTitle?.items || [];

      if (data.length === 0) return;

      setOptionsPosition({
        items: data.map((item: any) => ({
          label: item.jobName,
          value: item.id,
        })),
        pageInfo: res.data?.getJobTitle.pageInfo,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutationEmployee = useMutation({
    mutationFn: RewardInfoApis.getCbxEmployee,
    onSuccess: (res) => {
      const data = res?.data?.getUserReward?.items || [];

      if (data.length === 0) return;

      setOptionsEmployee({
        items: data.map((item: any) => ({
          label: `${item.id} - ${item.name}`,
          value: item.id,
        })),
        pageInfo: res.data?.getUserReward.pageInfo,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handlePositionSelectScrollChange = (value: ILazySelectParams) => {
    mutationPosition.mutate({
      skip: 0,
      take: value.pageSize,
      jobName: value.value,
    });
  };

  const handleEmployeeSelectScrollChange = (value: ILazySelectParams) => {
    mutationEmployee.mutate({
      skip: 0,
      take: value.pageSize,
      search: value.value,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue({
      ...defaultFilter,
      user: defaultFilter.employeeId,
    });

    onCancel();
  };

  const handleChangePlant = () => {
    form.setFieldValue('rewardId', null);

    getComboboxGroupRoleManagement({
      skip: 0,
      take: 20,
      plantId: form.getFieldValue('plantId'),
    });
  };

  const handleRewardScrollChange = (value: ILazySelectParams) => {
    const plantId = form.getFieldValue('plantId') ?? defaultFilter.plantId;

    getComboboxGroupRoleManagement({
      skip: 0,
      take: value.pageSize,
      rewardId: value.value,
      plantId: plantId === 'all' ? null : plantId,
    });
  };

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  useEffect(() => {
    form.setFieldsValue({
      ...defaultFilter,
      user: defaultFilter.employeeId,
    });
  }, [defaultFilter, form]);

  return (
    <Content isOpen={isOpen}>
      <FormFilter
        form={form}
        onFinish={onFinish}
        onCancel={handleCancel}
        disabled={disabled}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                loading={getAllPlantLoading}
                options={[
                  {
                    label: 'Tất cả chi nhánh',
                    value: 'all',
                  },
                  ...plants,
                ]}
                disabled={!(user?.isAdmin || user?.isHO)}
                placeholder="Chọn chi nhánh"
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã thưởng DMS" name="groupRoleManagementId">
              <LazySelectForm
                hasNextPage={rewardData.hasNextPage}
                customeSize={20}
                allowClear
                data={rewardData}
                placeholder="Chọn mã dự thưởng"
                loading={isPendingGetComboboxGroupRoleManagement}
                onSelectScrollChange={handleRewardScrollChange}
                showSearch
              />
            </Form.Item>
          </Col>
          {/* <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Đơn vị cấp" name="">
              <SelectForm
                options={optionsUnit}
                placeholder="Nhập mã đơn vị - đơn vị"
              />
            </Form.Item>
          </Col> */}
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chức vụ" name="jobTitleCode">
              <LazySelectForm
                customeSize={20}
                data={{
                  options: optionsPosition.items || [],
                  totalItems: optionsPosition.pageInfo?.totalItems || 0,
                }}
                loading={mutationPosition.isPending}
                placeholder="Chọn chức vụ"
                onSelectScrollChange={handlePositionSelectScrollChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Nhân viên" name="user">
              <LazySelectForm
                customeSize={20}
                data={{
                  options: optionsEmployee.items || [],
                  totalItems: optionsEmployee.pageInfo?.totalItems || 0,
                }}
                loading={mutationEmployee.isPending}
                placeholder="Chọn nhân viên"
                onSelectScrollChange={handleEmployeeSelectScrollChange}
                disabled={!(user?.isAdmin || user?.isHO || user?.isNS)}
              />
            </Form.Item>
          </Col>
          {/* <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Đối tượng" name="">
              <SelectForm
                options={optionsObject}
                placeholder="Chọn đối tượng"
              />
            </Form.Item>
          </Col> */}
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Nhóm" name="groupId">
              {/* <LazySelectForm
                customeSize={20}
                data={{
                  options: optionsGroup.items || [],
                  totalItems: optionsGroup.pageInfo?.totalItems || 0,
                }}
                loading={mutationGroup.isPending}
                placeholder="Chọn nhóm"
                onSelectScrollChange={handleGroupSelectScrollChange}
              /> */}
              <SelectForm
                options={groupRewardOptions}
                placeholder="Chọn chức vụ"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Trạng thái" name="status">
              <SelectForm options={optionStatus} placeholder="Trạng thái" />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterRewardInfo;
